import { MutableRefObject, useEffect, useRef } from 'react';
import {
  atom,
  atomFamily,
  SetterOrUpdater,
  useRecoilCallback,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import { useWindowSize } from '@react-hook/window-size';
import { useDebounceCallback } from '@react-hook/debounce';
import ResizeObserver from 'resize-observer-polyfill';
import * as K from 'common/constants';
import { recoilSelector } from 'common/util/recoil-selector';
import { settingsState } from './SettingsManager.state';

// TODO: maybe we can rewrite this in a more robust way, but ts does not allow optional chaining into "missing" properties
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResizeObserverEntry = any;

/**
 * Tracks window.innerWidth in global state
 */
export const windowWidthState = atom({
  key: K.GLOBAL_KEY.WINDOW_WIDTH_STATE,
  default: window?.innerWidth || 0,
});

/**
 * Tracks window.innerHeight in global state
 */
export const windowHeightState = atom({
  key: K.GLOBAL_KEY.WINDOW_HEIGHT_STATE,
  default: window?.innerHeight || 0,
});

/**
 * Tracks an element's width in global state
 */
export const elementWidthState = atomFamily({
  key: K.GLOBAL_KEY.ELEMENT_WIDTH_STATE,
  default: 0,
});

/**
 * Tracks an element's height in global state
 */
export const elementHeightState = atomFamily({
  key: K.GLOBAL_KEY.ELEMENT_HEIGHT_STATE,
  default: 0,
});

/**
 * Tracks the width of the vertical scrollbar for wrapped lines mode
 */
export const wrappedLinesScrollbarWidthState = atom<number>({
  key: K.RECOIL_ATOM_KEY.WRAPPED_LINES_SCROLLBAR_WIDTH,
  default: 0,
});

export const sideBarWidthSelector = recoilSelector<number>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_SIDEBAR_WIDTH,
  get: ({ get }) => {
    let sidebarWidth = get(elementWidthState(K.RESIZE_ID.SEARCH_LOGS_SIDEBAR));
    const settings = get(settingsState);
    // condition to check if sidebar width value is 0 in any case reset to default
    // width is set to 10 if sidebar is collapsed by default, otherwise 220
    if (sidebarWidth === 0 ) {
      sidebarWidth = settings[K.SETTING.SHOW_SIDEBAR] ? 220 : 10;
    }
    return sidebarWidth;
  },
});

/**
 * Calculates the remaining width to the right of the sidebar on the search page
 */
export const searchPageRemainingWidthSelector = recoilSelector<number>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_PAGE_REMAINING_WIDTH,
  get: ({ get }) =>
    get(windowWidthState) - get(sideBarWidthSelector) - get(elementWidthState(K.RESIZE_ID.SEARCH_LOG_DETAILS)),
});

export const graphPageRemainingWidthSelector = recoilSelector<number>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_GRAPH_PAGE_REMAINING_WIDTH,
  get: ({ get }) => get(windowWidthState) - get(elementWidthState(K.RESIZE_ID.SEARCH_LOGS_SIDEBAR)),
});

export const searchLegendWidthSelector = recoilSelector<number>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_GRAPH_LEGEND_WIDTH,
  get: ({ get }) => get(elementWidthState(K.RESIZE_ID.SEARCH_GRAPHS_LEGEND)),
});

/**
 * Calculates the width of a wrapped log line
 */
export const wrappedLineWidthSelector = recoilSelector<number>({
  key: K.RECOIL_SELECTOR_KEY.WRAPPED_LINE_WIDTH,
  get: ({ get }) => get(searchPageRemainingWidthSelector) - get(wrappedLinesScrollbarWidthState),
});

/**
 * Tracks a singleton ResizeObserver in global state
 */
const resizeObserverState = atom<ResizeObserver | null>({
  key: K.GLOBAL_KEY.RESIZE_OBSERVER_STATE,
  default: null,
});

/**
 * Components import and call this hook to register with the ResizeObserver. The hook returns a ref that the caller must
 * attach to the element to be observed.
 */
export const useResizeRegistration = (): MutableRefObject<HTMLDivElement | null> => {
  const ref = useRef<HTMLDivElement>(null);
  const resizeObserver = useRecoilValue(resizeObserverState);
  useEffect(useRecoilCallback(({set}) => () => {
    const id: K.RESIZE_ID = ref?.current?.dataset?.resizeId as K.RESIZE_ID;
    if (!id) {
      // If the ref can't be associated with an {@see RESIZE_ID}, return early
      return;
    }
    // Initialize the {@see elementWidthState} and {@see elementHeightState} values for the element to be observed
    const refCurrent = ref.current;
    const elementWidth = refCurrent?.offsetWidth ?? 0;
    const elementHeight = refCurrent?.offsetHeight ?? 0;
    set(elementWidthState(id), elementWidth);
    set(elementHeightState(id), elementHeight);
    // Register the element for observation
    resizeObserver?.observe?.(refCurrent as Element);
    return (): void => {
      // Unregister the element for observation
      resizeObserver?.unobserve?.(refCurrent as Element);
      // TODO: We might need to set the dimensions in global state to 0 here
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []), []);
  return ref;
};

/**
 * Layout state management component. Watches for changes to the dimensions of the window as well as elements that have
 * registered with the useResizeRegistration hook and sets the changes to those dimensions into global recoil state.
 * This component never renders content.
 *
 * To register an element with the ResizeObserver and import one or both of its dimensions from global state:
 *
 * 1. Create a new {@see RESIZE_ID}, and add it as a data-resize-id custom attribute to the element to be observed
 * 2. Call the useResizeRegistration hook, and attach the ref returned from the hook to the element
 * 3. Add the {@see RESIZE_ID} to the setElementWidth and setElementHeight maps
 * 4. Import the elementWidthState and/or elementHeightState atomFamily anywhere to subscribe to the changes
 */
function LayoutStateManager(): JSX.Element {

  // window dimension state
  const [ windowWidth, windowHeight ] = useWindowSize();
  const setWindowWidth = useSetRecoilState(windowWidthState);
  const setWindowHeight = useSetRecoilState(windowHeightState);

  // ResizeObserver state
  const setResizeObserver = useSetRecoilState(resizeObserverState);
  // map of resize ids to recoil state setters; the resize id is the input for the elementWidthState atomFamily
  const setElementWidth: Record<K.RESIZE_ID, SetterOrUpdater<number>> = {
    [K.RESIZE_ID.SEARCH_LOGS_SIDEBAR]: useSetRecoilState(elementWidthState(K.RESIZE_ID.SEARCH_LOGS_SIDEBAR)),
    [K.RESIZE_ID.SEARCH_GRAPHS_LEGEND]: useSetRecoilState(elementWidthState(K.RESIZE_ID.SEARCH_GRAPHS_LEGEND)),
    [K.RESIZE_ID.SEARCH_LOG_DETAILS]: useSetRecoilState(elementWidthState(K.RESIZE_ID.SEARCH_LOG_DETAILS)),
  };
  // map of resize ids to recoil state setters; the resize id is the input for the elementHeightState atomFamily
  const setElementHeight: Record<K.RESIZE_ID, SetterOrUpdater<number>> = {
    [K.RESIZE_ID.SEARCH_LOGS_SIDEBAR]: useSetRecoilState(elementHeightState(K.RESIZE_ID.SEARCH_LOGS_SIDEBAR)),
    [K.RESIZE_ID.SEARCH_GRAPHS_LEGEND]: useSetRecoilState(elementHeightState(K.RESIZE_ID.SEARCH_GRAPHS_LEGEND)),
    [K.RESIZE_ID.SEARCH_LOG_DETAILS]: useSetRecoilState(elementHeightState(K.RESIZE_ID.SEARCH_LOG_DETAILS)),
  };

  // set window.innerWidth to global state
  useEffect(() => {
    setWindowWidth(windowWidth);
  }, [ windowWidth, setWindowWidth ]);

  // set window.innerHeight to global state
  useEffect(() => {
    setWindowHeight(windowHeight);
  }, [ windowHeight, setWindowHeight ]);

  /**
   * Called for each ResizeObserverEntry registered to the ResizeObserver instance with ResizeObserver.observe. This
   * callback is debounced with the same hook use by the useWindowSize hook. The callback identifies the element being
   * observed by its {@see RESIZE_ID} and sets its width and height into global state. Reading dimensions from
   * borderBoxSize is preferred; contentRect has more browser support, but might be deprecated soon.
   * {@link https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry}
   */
  const debouncedObserverCallback = useDebounceCallback((entry: ResizeObserverEntry) => {
    const id: K.RESIZE_ID = entry?.target?.dataset?.resizeId;
    if (!id) {
      return;
    }
    const isSupported = entry?.borderBoxSize && entry.borderBoxSize.length > 0;
    const elementWidth = isSupported ? entry?.borderBoxSize?.[0]?.inlineSize : entry?.contentRect?.width;
    const elementHeight = isSupported ? entry?.borderBoxSize?.[0]?.blockSize : entry?.contentRect?.height;

    // TODO: we might want to update these only if they change. Components subscribed to recoil global state will
    // re-render even when setting an atom's value to the same primitive again
    setElementWidth[id](elementWidth || 0);
    setElementHeight[id](elementHeight || 0);
  });

  // initialize resizeObserver
  useEffect(() => {
    const resizeObserver: ResizeObserver = new ResizeObserver((entries) => {
      entries.forEach(debouncedObserverCallback);
    });
    setResizeObserver(resizeObserver);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <></>;

}

export default LayoutStateManager;
